import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Loader2, Package, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { DashboardLayout } from '@/components/florist-dashboard/DashboardLayout';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { OrderStatusActions } from '@/components/florist-dashboard/orders/OrderStatusActions';
import { cn } from '@/lib/utils';
import { OrderStatusTimeline } from "@/components/shared/OrderStatusTimeline";

interface Order {
  id: string;
  created_at: string;
  customer_id: string | null;
  florist_id: string;
  status: 'pending' | 'confirmed' | 'preparing' | 'out_for_delivery' | 'delivered' | 'cancelled' | 'delayed' | 'refunded';
  total_amount: number;
  commission_amount: number;
  delivery_address: string;
  delivery_date: string;
  delivery_time: string;
  delivery_type: string | null;
  order_number: string | null;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  items: any[];
  special_instructions: string | null;
  gift_message: string | null;
  is_gift: boolean;
  payment_status: string | null;
  is_same_day: boolean;
  buyer_first_name: string | null;
  buyer_last_name: string | null;
  buyer_email: string | null;
  buyer_phone: string | null;
  recipient_first_name: string | null;
  recipient_last_name: string | null;
  recipient_phone: string | null;
  suburb: string;
  state: string;
  postcode: string;
}

export default function Orders() {
  const { user } = useAuth();
  const [statusFilter, setStatusFilter] = useState<Order['status'] | 'all'>('all');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const navigate = useNavigate();

  // First fetch the store profile
  const { data: storeProfile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ['store-profile', user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error('No user ID');
      const { data, error } = await supabase
        .from('store_profiles')
        .select('*')
        .eq('florist_id', user.id as any)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  // Then fetch orders using the store profile ID
  const { data: orders, isLoading: isLoadingOrders, refetch } = useQuery({
    queryKey: ['orders', storeProfile?.id, statusFilter],
    queryFn: async () => {
      if (!storeProfile?.id) throw new Error('No store profile ID');

      console.log('Using store profile ID for orders query:', storeProfile.id);

      let query = supabase
        .from('orders')
        .select('*')
        .eq('florist_id', storeProfile.id as any);

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter as any);
      }
      
      // Add proper sorting to ensure newest orders appear first
      query = query.order('created_at', { ascending: false });

      const { data, error } = await query;
      
      if (error) {
        console.error('Error fetching orders:', error);
        throw error;
      }

      console.log('Orders query result:', data);
      return data as any as Order[];
    },
    enabled: !!storeProfile?.id,
  });

  // Function to view order details
  const viewOrderDetails = (orderId: string) => {
    navigate(`/florist-dashboard/orders/${orderId}`);
  };

  // Add a refresh function
  const refreshOrders = async () => {
    setIsRefreshing(true);
    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  };

  if (isLoadingProfile || isLoadingOrders) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (!storeProfile) {
    return (
      <DashboardLayout>
        <div className="space-y-8">
          <div>
            <h1 className="text-3xl font-bold text-brand-primary">Orders</h1>
            <p className="text-brand-primary/70 mt-1">
              Please complete your store profile setup to view orders
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (orders === undefined) {
    return (
      <DashboardLayout>
        <div className="space-y-8">
          <div>
            <h1 className="text-3xl font-bold text-brand-primary">Orders</h1>
            <p className="text-brand-primary/70 mt-1">
              Error loading orders. Please check the console for details.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const getStatusColor = (status: Order['status']) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'confirmed':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'preparing':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'out_for_delivery':
        return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'delivered':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'delayed':
        return 'bg-[#843329]/10 text-[#843329] border-[#843329]/20';
      case 'refunded':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-brand-primary">Orders</h1>
          <p className="text-brand-primary/70 mt-1">
            Manage and track your customer orders
          </p>
        </div>

        <div className="bg-white p-6 rounded-lg border border-brand-primary/10 shadow-sm">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-lg font-medium text-brand-primary">
                Order List {orders && orders.length > 0 && <span className="text-sm font-normal text-brand-primary/70">({orders.length})</span>}
              </h2>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={refreshOrders}
                disabled={isRefreshing}
                className="border-brand-primary/10"
              >
                {isRefreshing ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Refreshing...</>
                ) : (
                  <><RefreshCw className="h-4 w-4 mr-2" /> Refresh</>
                )}
              </Button>
              <Button 
                variant="outline"
                size="sm" 
                onClick={() => window.location.reload()}
                className="border-brand-primary/10"
              >
                Force Reload
              </Button>
              <Select
                value={statusFilter}
                onValueChange={(value) => setStatusFilter(value as Order['status'] | 'all')}
              >
                <SelectTrigger className="w-[180px] border-brand-primary/10">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Orders</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="preparing">Preparing</SelectItem>
                  <SelectItem value="out_for_delivery">Out for Delivery</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="delayed">Delayed</SelectItem>
                  <SelectItem value="refunded">Refunded</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-4">
            {orders?.map((order) => (
              <div
                key={order.id}
                className="bg-white p-6 rounded-lg shadow-sm border border-brand-primary/10 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-semibold text-brand-primary">
                      {order.order_number ? `Order #${order.order_number}` : `Order ${order.id.substring(0, 8)}`}
                    </h3>
                    <p className="text-sm text-brand-primary/70">
                      {new Date(order.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-brand-primary">${order.total_amount.toFixed(2)}</p>
                    <div className="flex gap-2 justify-end">
                      <Badge className={`${getStatusColor(order.status)}`}>
                        {order.status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                      </Badge>
                      {order.delivery_type === 'pickup' && (
                        <Badge className="bg-purple-100 text-purple-800 border-purple-200 font-bold text-xs px-3 py-1">
                          🌸 PICKUP
                        </Badge>
                      )}
                      {order.is_same_day && (
                        <Badge variant="outline" className="border-[#843329] text-[#843329]">
                          Same Day
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <h4 className="font-medium mb-2 text-brand-primary/90">Order Items</h4>
                    <div className="space-y-2">
                      {order.items?.map((item: any, index: number) => (
                        <div key={index} className="flex items-start gap-3">
                          {item.image_url && (
                            <img 
                              src={item.image_url} 
                              alt={item.name} 
                              className="w-12 h-12 object-cover rounded-md flex-shrink-0"
                            />
                          )}
                          <div className="flex-grow">
                            <p className="text-sm font-medium text-brand-primary">
                              {item.name}
                              {item.size && item.size !== 'N/A' ? <span className="text-xs text-brand-primary/70"> ({item.size})</span> : ''}
                            </p>
                            <p className="text-xs text-brand-primary/70">Qty: {item.quantity ?? 1}</p>
                            <p className="text-xs text-brand-primary/70">${item.price?.toFixed(2) ?? '0.00'}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium mb-2 text-brand-primary/90">Customer Details</h4>
                    <div className="space-y-1">
                      <p className="text-sm text-brand-primary">
                        {order.buyer_first_name && order.buyer_last_name 
                          ? `${order.buyer_first_name} ${order.buyer_last_name}`
                          : order.customer_name || 'Unknown Name'}
                      </p>
                      <p className="text-sm text-brand-primary/70">
                        {order.buyer_email || order.customer_email || 'No email'}
                      </p>
                      <p className="text-sm text-brand-primary/70">
                        {order.buyer_phone || order.customer_phone || 'No phone'}
                      </p>
                    </div>
                    {(order.recipient_first_name || order.recipient_last_name || order.recipient_phone) && (
                        <div className="mt-3 pt-3 border-t border-brand-primary/10">
                          <h4 className="font-medium mb-2 text-brand-primary/90">Recipient Details</h4>
                          <p className="text-sm text-brand-primary/70">
                            {order.recipient_first_name} {order.recipient_last_name}
                            {order.recipient_phone && <span className="block">{order.recipient_phone}</span>}
                          </p>
                        </div>
                      )}
                  </div>

                  <div>
                    <h4 className="font-medium mb-2 text-brand-primary/90">Delivery Details</h4>
                    <div className="space-y-2">
                      <p className="text-lg font-bold text-brand-primary">
                        {new Date(order.delivery_date).toLocaleDateString('en-AU', {
                          day: 'numeric',
                          month: 'numeric',
                          year: 'numeric'
                        })}
                      </p>
                      <div className="text-sm text-brand-primary/70">
                        {(() => {
                          try {
                            const addr = JSON.parse(order.delivery_address);
                            return (
                              <>
                                <p>{addr.street}</p>
                                <p>{addr.suburb}, {addr.state} {addr.postcode}</p>
                              </>
                            );
                          } catch {
                            return <p>{order.delivery_address}</p>;
                          }
                        })()}
                      </div>
                      {order.special_instructions && (
                        <div className="mt-2 pt-2 border-t border-brand-primary/10">
                          <p className="text-sm font-medium text-brand-primary">Instructions & Gift Msg:</p>
                          <p className="text-sm text-brand-primary/70 whitespace-pre-wrap">{order.special_instructions}</p>
                        </div>
                      )}
                      {order.is_gift && order.gift_message && (
                        <div className="mt-2 pt-2 border-t border-brand-primary/10">
                          <p className="text-sm font-medium text-brand-primary">Gift Message (Separate):</p>
                          <p className="text-sm text-brand-primary/70 italic whitespace-pre-wrap">{order.gift_message}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-brand-primary/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4 md:order-1">
                    <div className="flex items-center gap-2 flex-shrink-0">
                    <p className="text-sm font-medium">Status:</p>
                    <Badge
                      variant="outline"
                      className={cn(
                        "capitalize",
                        order.status === "pending" && "border-yellow-500 text-yellow-500",
                        order.status === "rejected" && "border-red-500 text-red-500"
                      )}
                    >
                        {order.status.replace("_", " ")}
                    </Badge>
                  </div>
                    <div className="flex flex-wrap gap-2 justify-start flex-shrink-0">
                    <Button
                      variant="outline"
                      onClick={() => viewOrderDetails(order.id)}
                      className="border-black text-black hover:bg-gray-100"
                    >
                      View Details
                    </Button>
                    <OrderStatusActions 
                      orderId={order.id}
                      currentStatus={order.status}
                    />
                    </div>
                  </div>

                  <div className="flex-grow min-w-0 md:order-2">
                    <OrderStatusTimeline currentStatus={order.status} createdAt={order.created_at} orientation="horizontal" />
                  </div>
                </div>
              </div>
            ))}

            {orders?.length === 0 && (
              <div className="text-center py-12 bg-[#967F81]/5 rounded-lg border border-brand-primary/10">
                <div className="bg-[#967F81]/5 p-4 rounded-full mb-4 mx-auto w-fit">
                  <Package className="h-8 w-8 text-brand-primary/60" />
                </div>
                <p className="text-brand-primary/70 font-medium mb-1">No orders found</p>
                <p className="text-sm text-brand-primary/50">
                  {statusFilter !== 'all' 
                    ? `No ${statusFilter.split('_').join(' ')} orders found. Try a different filter.` 
                    : 'You have no orders yet. They will appear here when customers place orders.'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
} 